/**
 * @file useDailyArticlesSwr.ts
 */

import { useEffect, useReducer, type Dispatch } from 'react'

import { readDailyArticlesCache, revalidateDailyArticles } from '@shared/lib/browser-cache/dailyArticlesCache'

import {
  createInitialDailyArticlesState,
  dailyArticlesReducer,
  type DailyArticlesAction,
  selectDailyCarouselSkeleton,
  selectDailyCarouselVisible,
} from '../model/dailyArticlesView'

type Params = {
  loadEnabled?: boolean
}

/**
 * @function runHydrateAndRevalidate
 * @description функция для загрузки данных из кеша и ревалидации данных
 */
const runHydrateAndRevalidate = (dispatch: Dispatch<DailyArticlesAction>, isCancelled: () => boolean) => {
  const cached = readDailyArticlesCache()
  const softFail = !!cached?.length

  const hydrateSteps = [
    cached ? () => dispatch({ type: 'apply_cache', articles: cached }) : null,
    () => dispatch({ type: 'prepare_revalidate', hasCache: softFail }),
  ].filter((step): step is () => void => step != null)

  hydrateSteps.forEach((step) => step())

  return revalidateDailyArticles()
    .then((list) => {
      if (isCancelled()) return
      dispatch({ type: 'revalidate_done', list, softFail })
    })
    .catch(() => {
      if (isCancelled()) return
      dispatch({ type: 'revalidate_failed', softFail })
    })
}

/**
 * @function useDailyArticlesSwr
 * @description хук для работы с данными блока «Афиша Daily» на главной и смежных страницах
 */
export const useDailyArticlesSwr = ({ loadEnabled = true }: Params = {}) => {
  const [state, dispatch] = useReducer(dailyArticlesReducer, undefined, createInitialDailyArticlesState)

  useEffect(() => {
    if (!loadEnabled) return

    let cancelled = false
    const isCancelled = () => cancelled

    runHydrateAndRevalidate(dispatch, isCancelled)

    const handlePageShow = ({ persisted }: PageTransitionEvent) =>
      persisted && runHydrateAndRevalidate(dispatch, isCancelled)

    window.addEventListener('pageshow', handlePageShow)

    return () => {
      cancelled = true
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [loadEnabled])

  return {
    articles: state.articles,
    isVisible: selectDailyCarouselVisible(state),
    showSkeleton: selectDailyCarouselSkeleton(state),
  }
}
